import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Network, Plus, Search, SearchX, ShieldCheck, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Highlight } from "@/components/Highlight";
import { LoadTestCard } from "@/components/LoadTestCard";
import { AlertsCard } from "@/components/AlertsCard";
import { CustomerNotificationsCard } from "@/components/CustomerNotificationsCard";
import { EndCustomerMailCard } from "@/components/EndCustomerMailCard";
import { DeliveryAddressesCard } from "@/components/DeliveryAddressesCard";
import { StaffMfaCard } from "@/components/StaffMfaCard";
import { Badge, Button, Card, cx, EmptyState, Input, Kbd, Skeleton, Tip } from "@/components/ui";
import { api, ApiError, isDemo, type AdminTenant, type FfnLink, type FfnMerchants, type StaffRole } from "@/lib/api";
import { signupOfferLines } from "@logiful/domain";
import { ProspectAccessCard } from "@/components/ProspectAccessCard";
import { OverdueWarningCard } from "@/components/OverdueWarningCard";
import { fmtRelative } from "@/lib/format";
import { AssignToCustomerDialog, ffnLinkError, FfnLinkDialog } from "./FfnLink";
import { InviteDialog } from "./Team";
import { WarehouseSitesCard } from "@/components/WarehouseSitesCard";
import { intlLocale } from "@/lib/i18n";

const STAFF_ROLES: Record<StaffRole, { label: string; text: string }> = {
  staff: { label: "Logiful-Team", text: "Alle Kunden sehen und bearbeiten" },
  staff_admin: { label: "Logiful-Admin", text: "Zusätzlich Kunden anlegen und Logiful-Team verwalten" },
};

type Merchant = FfnMerchants["merchants"][number];
type MerchantFilter = "all" | "open" | "pending" | "active" | "inactive";
const MERCHANT_FILTERS: { key: MerchantFilter; label: string; test: (m: Merchant) => boolean }[] = [
  { key: "all", label: "Alle", test: () => true },
  { key: "open", label: "Noch nicht im Portal", test: (m) => m.active && !m.portal },
  { key: "pending", label: "Freigabe ausstehend", test: (m) => !!m.portal && m.portal.status !== "active" },
  { key: "active", label: "Verbunden", test: (m) => m.portal?.status === "active" },
  { key: "inactive", label: "Inaktiv", test: (m) => !m.active },
];
/** Suche ohne Rücksicht auf Groß-/Kleinschreibung und Umlaute: "sud" findet "Süd", "strasse" findet "Straße" */
const norm = (s: string) => s.toLowerCase().normalize("NFKD").replace(/\p{Diacritic}/gu, "").replace(/ß/g, "ss");

const slugify = (s: string) => s.toLowerCase().normalize("NFKD").replace(/\p{Diacritic}/gu, "").replace(/ß/g, "ss").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

function FfnStatus({ link }: { link: FfnLink | undefined }) {
  if (!link?.merchantId && !link?.authorized) return <Badge>FFN offen</Badge>;
  if (!link.authorized) return <Badge tone="warning">Freigabe ausstehend</Badge>;
  if (link.status === "active") return <Badge tone="success">{link.warehouseName ?? "FFN verbunden"}</Badge>;
  return <Badge tone="warning">Lager wählen</Badge>;
}

export function CustomersPage() {
  const { me, setTenantId } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-tenants"], queryFn: api.admin.tenants });
  const links = useQuery({ queryKey: ["admin-ffn-links"], queryFn: api.admin.ffnLinks });
  const [open, setOpen] = useState(false);
  const [linking, setLinking] = useState<AdminTenant | null>(null);
  const isAdmin = me.user.staffRole === "staff_admin";
  const [params, setParams] = useSearchParams();

  // Rückmeldung, wenn Logiful sich selbst mit dem Kundenzugang bei JTL angemeldet hat
  useEffect(() => {
    const err = params.get("error");
    const t = params.get("tenant");
    if (!err && !params.get("connected")) return;
    if (t && !q.data) return; // warten, bis die Kunden geladen sind
    const switched = params.get("switched");
    if (params.get("connected") === "1" && switched)
      // Im Dialog eines anderen Kunden mit diesem Konto angemeldet → Portal hat den richtigen Kunden verbunden
      toast.success(`„${switched}“ verbunden`, { description: `Angemeldet war FFN-Konto ${params.get("ffn")} – das gehört zu „${switched}“, nicht zu „${params.get("from")}“. „${params.get("from")}“ wartet weiter auf seine eigene Freigabe.`, duration: 15_000 });
    else if (params.get("connected") === "1") toast.success("FFN-Konto des Kunden verbunden", { description: params.get("choose") ? "Bitte noch das Lager wählen." : "Der Erstabgleich läuft." });
    if (err) toast.error(ffnLinkError(err, params), { duration: 15_000 });
    const tenant = t ? q.data?.find((x) => x.id === t) : undefined;
    if (tenant) setLinking(tenant);
    void qc.invalidateQueries({ queryKey: ["admin-ffn-links"] });
    setParams({}, { replace: true });
  }, [params, setParams, q.data, qc]);

  return (
    <div className="pb-16">
      <PageHeader
        title="Kunden"
        description="Alle Mandanten im Logiful-Portal. Ein Klick wechselt in die Ansicht des Kunden."
        actions={isAdmin ? <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>Kunde anlegen</Button> : undefined}
      />
      <div className="px-4 md:px-8">
        <AlertsCard />
        <Card className="overflow-hidden">
          {/* feste Spalten: Kunde · Kürzel · Personen · Kunde seit · Auto-Freigabe · FFN */}
          <div className="hidden md:grid md:grid-cols-[minmax(0,1fr)_96px_120px_76px_96px_88px_96px_210px] gap-x-4 px-4 h-9 items-center border-b border-border bg-surface-2/50 text-[12px] font-medium text-fg-subtle">
            <span>Kunde</span>
            <Tip content="Logiful-Kundennummer – Schlüssel für alle Systeme (Wawi, FFN, Retouren, Retourenportal). Neue Kunden ab 10000, Bestandskunden mit ihrer Wawi-Nummer."><span className="w-fit">Kd-Nr.</span></Tip>
            <span>Kürzel</span><span className="text-right">Personen</span><span>Kunde seit</span>
            <Tip content="Übertragene Artikel automatisch in FFN für Logiful freigeben – nur mit Zustimmung des Kunden"><span className="w-fit">Auto-Freigabe</span></Tip>
            <Tip content="Hardlock für die Umstellung: nichts ans Lager, keine Bestände und kein Standort an Shops – Auftragseingang, Regeln und Retouren gefahrlos prüfen"><span className="w-fit">Testbetrieb</span></Tip>
            <span>FFN-Verknüpfung</span>
          </div>
          {q.isLoading && <div className="p-4 space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>}
          <ul className="divide-y divide-border">
            {q.data?.map((t) => {
              const link = links.data?.find((l) => l.tenantId === t.id);
              return (
                <li key={t.id} className="grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_96px_120px_76px_96px_88px_96px_210px] items-center gap-x-4 px-4 py-2.5 hover:bg-surface-2/50">
                  <button onClick={() => { setTenantId(t.id); location.assign("/"); }} className="min-w-0 flex items-center gap-3 text-left">
                    <span className="size-9 shrink-0 rounded-lg bg-ink text-ink-fg grid place-items-center font-bold text-[13px]">{t.name.slice(0, 1)}</span>
                    <span className="min-w-0">
                      <span className="block font-medium truncate">{t.name}</span>
                      <span className="block text-[12px] text-fg-muted truncate font-mono">{t.slug}</span>
                      {t.signup && <SignupBadge s={t.signup} />}
                    </span>
                  </button>
                  <div className="hidden md:block"><CustomerNumberButton t={t} /></div>
                  <div className="hidden md:block"><TenantCodeButton t={t} /></div>
                  <span className="hidden md:block text-right text-[13px] tabular-nums text-fg-muted">{t.members}</span>
                  <span className="hidden md:block text-[13px] tabular-nums text-fg-muted">{new Date(t.createdAt).toLocaleDateString(intlLocale())}</span>
                  <div className="hidden md:block"><AutoReleaseToggle t={t} /></div>
                  <div className="hidden md:block"><TestModeToggle t={t} /></div>
                  <button onClick={() => setLinking(t)} className="min-w-0 text-left rounded-lg -mx-1 px-1 py-0.5 hover:bg-surface-2" title="FFN-Verknüpfung verwalten" aria-label={`FFN-Verknüpfung von ${t.name} verwalten`}>
                    {links.isLoading ? <Skeleton className="h-5 w-24" /> : <FfnStatus link={link} />}
                    {link?.merchantId && (
                      <span className="block mt-0.5 text-[11.5px] text-fg-subtle truncate">
                        Konto <span className="font-mono">{link.merchantId}</span>{link.merchantCompany && link.merchantCompany !== t.name ? ` · ${link.merchantCompany}` : ""}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
        <p className="mt-3 text-[12px] text-fg-subtle">Auf den FFN-Status klicken, um einen Kunden mit seinem eigenen JTL-FFN-Konto zu verknüpfen.</p>
        {q.data && <ProspectAccessCard admin={isAdmin} tenants={q.data} />}
        {q.data && <OverdueWarningCard admin={isAdmin} tenants={q.data} />}
      </div>
      <NewCustomerDialog open={open} onOpenChange={setOpen} onCreated={(id) => {
        void qc.invalidateQueries({ queryKey: ["admin-tenants"] });
        // Direkt weiter zur FFN-Verknüpfung – der übliche nächste Schritt
        setLinking({ id, name: "Neuer Kunde", slug: "", createdAt: new Date().toISOString(), members: 0 });
      }} />
      {isAdmin && <div className="px-4 md:px-8"><CustomerNotificationsCard /><EndCustomerMailCard /><WarehouseSitesCard admin={isAdmin} /><DeliveryAddressesCard admin={isAdmin} /><StaffMfaCard /></div>}
      {isAdmin && <div className="px-4 md:px-8"><LoadTestCard /></div>}
      {linking && <FfnLinkDialog key={linking.id} tenant={q.data?.find((t) => t.id === linking.id) ?? linking} open onOpenChange={(o) => !o && setLinking(null)} />}
    </div>
  );
}

export function NewCustomerDialog({ open, onOpenChange, onCreated, initialName = "", initialEmail = "" }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: (id: string) => void; initialName?: string; initialEmail?: string }) {
  const [name, setName] = useState(initialName);
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [adminEmail, setAdminEmail] = useState(initialEmail);
  const [outsideEu, setOutsideEu] = useState(false);
  const [customerNumber, setCustomerNumber] = useState("");
  const next = useQuery({ queryKey: ["next-customer-number"], queryFn: api.admin.nextCustomerNumber, enabled: open, staleTime: 0 });
  const effectiveSlug = slugTouched ? slug : slugify(name);
  const validEmail = !adminEmail || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(adminEmail.trim());
  const valid = name.trim().length >= 2 && /^[a-z0-9][a-z0-9-]{1,40}$/.test(effectiveSlug) && validEmail;

  const create = useMutation({
    mutationFn: () => api.admin.createTenant({ name: name.trim(), slug: effectiveSlug, ...(adminEmail ? { adminEmail: adminEmail.trim().toLowerCase() } : {}), shipsOutsideEu: outsideEu, ...(customerNumber ? { customerNumber } : {}) }),
    onSuccess: (r) => {
      toast.success(`${name.trim()} angelegt`, { description: r.inviteSent ? `Einladung an ${adminEmail.trim()} verschickt` : adminEmail ? "Einladung konnte nicht versendet werden (E-Mail-Versand nicht eingerichtet)" : undefined });
      setName(""); setSlug(""); setSlugTouched(false); setAdminEmail(""); setOutsideEu(false);
      onCreated(r.id);
      onOpenChange(false);
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Anlegen fehlgeschlagen") : "Anlegen fehlgeschlagen"),
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(480px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2"><Building2 className="size-4 text-fg-subtle" /> Neuen Kunden anlegen</Dialog.Title>
          <Dialog.Description className="text-fg-muted mt-1">Der Kunden-Admin bekommt eine Einladung und kann danach sein Team selbst verwalten.</Dialog.Description>
          <div className="mt-5 space-y-3">
            <label className="block"><span className="text-[12px] font-medium text-fg-muted">Firmenname</span><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Nordlicht Naturkosmetik GmbH" className="mt-1" /></label>
            <label className="block">
              <span className="text-[12px] font-medium text-fg-muted">Kürzel</span>
              <Input value={effectiveSlug} onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()); }} placeholder="nordlicht" className="mt-1 font-mono" />
              <span className="text-[11.5px] text-fg-subtle">Interne Kennung, z. B. für Auswertungen. Nur a–z, 0–9, Bindestrich.</span>
            </label>
            <label className="block">
              <span className="text-[12px] font-medium text-fg-muted">Kundennummer</span>
              <Input value={customerNumber} onChange={(e) => setCustomerNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 9))} placeholder={next.data ? `${next.data.next} (nächste freie)` : "wird vergeben"} className="mt-1 font-mono" />
              <span className="text-[11.5px] text-fg-subtle">Leer lassen: Connect vergibt die nächste freie Nummer (ab 10000). Kunde schon in der Wawi angelegt? Dann dessen Wawi-Kundennummer eintragen.</span>
            </label>
            <label className="block"><span className="text-[12px] font-medium text-fg-muted">E-Mail des Kunden-Admins (optional)</span><Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="inhaber@kunde.de" className="mt-1" invalid={!validEmail} /></label>
            <button role="switch" aria-checked={outsideEu} onClick={() => setOutsideEu(!outsideEu)} className="flex items-center gap-2.5 pt-1">
              <span className={cx("w-8 h-[18px] rounded-full p-0.5 transition-colors", outsideEu ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", outsideEu && "translate-x-3.5")} /></span>
              <span className="text-[13px]">Versendet auch außerhalb der EU <span className="text-fg-subtle">(Zolldaten werden Pflicht)</span></span>
            </button>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">Abbrechen</Button></Dialog.Close>
            <Button variant="primary" disabled={!valid} loading={create.isPending} onClick={() => create.mutate()}>Kunde anlegen</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function StaffPage() {
  const { me } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-staff"], queryFn: api.admin.staff });
  const [open, setOpen] = useState(false);
  const isAdmin = me.user.staffRole === "staff_admin";
  return (
    <div className="pb-16">
      <PageHeader title="Logiful-Team" description="Interne Mitarbeiter mit Zugriff auf alle Kunden." actions={isAdmin ? <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>Kollegen einladen</Button> : undefined} />
      <div className="px-4 md:px-8">
        <Card className="overflow-hidden">
          {q.isLoading && <div className="p-4"><Skeleton className="h-10 w-full" /></div>}
          <ul className="divide-y divide-border">
            {q.data?.map((s) => (
              <li key={s.userId} className="flex items-center gap-3 px-4 py-3">
                <span className="size-9 rounded-full bg-surface-2 border border-border grid place-items-center font-semibold text-[13px]">{(s.name ?? s.email).slice(0, 1).toUpperCase()}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{s.name ?? s.email}</div>
                  <div className="text-[12px] text-fg-muted truncate">{s.name ? `${s.email} · ` : ""}{s.lastLoginAt ? `zuletzt aktiv ${fmtRelative(s.lastLoginAt)}` : "noch nie angemeldet"}</div>
                </div>
                {s.disabledAt ? <Badge>gesperrt</Badge> : <Badge tone={s.role === "staff_admin" ? "coral" : "neutral"} icon={s.role === "staff_admin" ? <ShieldCheck className="size-3" /> : undefined}>{STAFF_ROLES[s.role ?? "staff"].label}</Badge>}
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <InviteDialog<StaffRole> open={open} onOpenChange={setOpen} roles={STAFF_ROLES} defaultRole="staff" title="Kollegen ins Logiful-Team einladen" onInvite={async (email, role) => { await api.admin.inviteStaff(email, role); void qc.invalidateQueries({ queryKey: ["admin-staff"] }); }} />
    </div>
  );
}

/** Logiful als Fulfiller: alle in FFN verknüpften Händler und ihr Stand im Portal */
export function FfnMerchantsPage() {
  const { setTenantId, me } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-ffn"], queryFn: api.admin.ffnMerchants });
  const [create, setCreate] = useState<FfnMerchants["merchants"][number] | null>(null);
  const [assigning, setAssigning] = useState<FfnMerchants["merchants"][number] | null>(null);
  const [linking, setLinking] = useState<Pick<AdminTenant, "id" | "name"> | null>(null);
  const d = q.data;
  const missing = d?.merchants.filter((m) => m.active && !m.portal).length ?? 0;
  const [params, setParams] = useSearchParams();
  const isAdmin = me.user.staffRole === "staff_admin";
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<MerchantFilter>("all");
  const searchRef = useRef<HTMLInputElement>(null);

  // "/" springt in die Suche
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const terms = norm(search).split(/\s+/).filter(Boolean);
  const bySearch = (d?.merchants ?? []).filter((m) => {
    if (!terms.length) return true;
    const hay = norm([m.company, m.merchantId, m.city, m.email, m.portal?.tenantName].filter(Boolean).join(" "));
    return terms.every((t) => hay.includes(t));
  });
  const counts = Object.fromEntries(MERCHANT_FILTERS.map((f) => [f.key, bySearch.filter(f.test).length])) as Record<MerchantFilter, number>;
  const shown = bySearch.filter(MERCHANT_FILTERS.find((f) => f.key === filter)!.test);
  const needles = search.toLowerCase().split(/\s+/).filter(Boolean);

  // Rückmeldung aus dem JTL-Login
  useEffect(() => {
    const err = params.get("error");
    if (params.get("connected") === "1") toast.success("Fulfiller-Konto verbunden", { description: "Die verknüpften Händler werden geladen." });
    if (err) toast.error(err === "denied" ? "Zugriff bei JTL abgelehnt" : err === "ffn_not_configured" ? "OAuth-Client fehlt (FFN_CLIENT_ID/SECRET)" : err === "forbidden" ? "Nur Logiful-Admins können das Fulfiller-Konto verbinden" : "Verbindung fehlgeschlagen – bitte erneut versuchen");
    if (params.get("connected") || err) setParams({}, { replace: true });
  }, [params, setParams]);

  const connect = () => {
    if (isDemo) return toast.info("Demo: Hier meldest du dich bei JTL mit dem Logiful-Fulfiller-Konto an.");
    location.href = api.admin.fulfillerConnectUrl;
  };
  const disconnect = useMutation({ mutationFn: api.admin.disconnectFulfiller, onSuccess: () => { toast.success("Fulfiller-Konto getrennt"); void qc.invalidateQueries({ queryKey: ["admin-ffn"] }); } });

  return (
    <div className="pb-16">
      <PageHeader
        title="FFN-Händler"
        description={<>Alle Händler, die in JTL-FFN mit Logiful als Fulfiller verknüpft sind – und ob sie schon über das Portal laufen.{d?.fulfillerUserId && <span className="block mt-1 text-[12px] text-fg-subtle">Verbunden als Logiful-Konto <span className="font-mono">{d.fulfillerUserId}</span>{d.connectedAt && <> · seit {new Date(d.connectedAt).toLocaleDateString(intlLocale())}</>}</span>}</>}
        actions={d?.fulfillerConnected && isAdmin ? <Button size="sm" variant="ghost" onClick={() => { if (confirm("Fulfiller-Konto wirklich trennen?")) disconnect.mutate(); }}>Fulfiller-Konto trennen</Button> : undefined}
      />
      <div className="px-4 md:px-8 space-y-4">
        {q.isLoading && <Skeleton className="h-40 w-full" />}
        {d && !d.clientConfigured && (
          <Card className="p-5">
            <h2 className="font-semibold">OAuth-Client fehlt</h2>
            <p className="mt-1 text-[13px] text-fg-muted">Im JTL-Kundencenter → OAuth → OAuth Clients einen Client „Logiful Connect“ anlegen (Redirect <code className="font-mono">{location.origin}/ffn/callback</code>, Scopes <code className="font-mono">ffn.merchant.read/write</code> und <code className="font-mono">ffn.fulfiller.read/write</code>) und ID/Secret als <code className="font-mono">FFN_CLIENT_ID</code>/<code className="font-mono">FFN_CLIENT_SECRET</code> in Render eintragen.</p>
          </Card>
        )}
        {d && d.clientConfigured && !d.fulfillerConnected && (
          <Card className="p-6">
            <h2 className="text-[16px] font-semibold">Logiful-Fulfiller-Konto verbinden</h2>
            <p className="mt-1 text-fg-muted">Einmalig mit dem Logiful-Konto bei JTL anmelden. Danach siehst du hier alle Händler, die in FFN mit euch verknüpft sind, und den Stand ihrer Umstellung aufs Portal.</p>
            {isAdmin ? (
              <Button variant="primary" className="mt-5 h-10 px-5" icon={<Network className="size-4" />} onClick={connect}>Mit JTL anmelden (Fulfiller)</Button>
            ) : (
              <p className="mt-4 text-[12.5px] text-fg-muted">Nur Logiful-Admins können das Fulfiller-Konto verbinden.</p>
            )}
          </Card>
        )}
        {d?.error && (
          <div className="rounded-xl border border-danger/25 bg-danger-soft px-4 py-3 text-[13px] text-danger flex items-center gap-3">
            <span className="flex-1">{d.error}</span>
            {isAdmin && <Button size="sm" onClick={connect}>Neu verbinden</Button>}
          </div>
        )}
        {d && d.fulfillerConnected && !d.error && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {([["all", "Verknüpfte Händler", d.merchants.length], ["active", "Im Portal angebunden", d.merchants.filter((m) => m.portal?.status === "active").length], ["open", "Noch umzustellen", missing]] as const).map(([key, label, n]) => (
                <button key={key} onClick={() => setFilter(filter === key ? "all" : key)} className={cx("text-left rounded-xl border bg-surface p-4 transition-colors hover:border-border-strong", filter === key && key !== "all" ? "border-ink ring-1 ring-ink" : "border-border")}>
                  <div className="text-[12.5px] text-fg-muted">{label}</div>
                  <div className={cx("mt-1 text-[26px] font-semibold tabular-nums", key === "open" && n > 0 && "text-warning")}>{n}</div>
                </button>
              ))}
            </div>
            <div className="flex flex-col lg:flex-row lg:items-center gap-2">
              <Input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") setSearch(""); }}
                placeholder="Händler suchen: Firma, FFN-ID, Ort, E-Mail, Kunde …"
                aria-label="FFN-Händler durchsuchen"
                className="h-10 lg:max-w-md flex-1"
                leading={<Search className="size-4" />}
                suffix={search ? <button onClick={() => { setSearch(""); searchRef.current?.focus(); }} className="size-6 grid place-items-center rounded-md hover:bg-surface-2 hover:text-fg" aria-label="Suche leeren"><X className="size-3.5" /></button> : <Kbd>/</Kbd>}
              />
              <div className="inline-flex self-start p-0.5 rounded-lg bg-surface-2 border border-border overflow-x-auto max-w-full">
                {MERCHANT_FILTERS.map((f) => (
                  <button key={f.key} onClick={() => setFilter(f.key)} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium flex items-center gap-1.5 whitespace-nowrap", filter === f.key ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>
                    {f.label} <span className="text-fg-subtle tabular-nums">{counts[f.key]}</span>
                  </button>
                ))}
              </div>
            </div>
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="text-[12px] text-fg-subtle bg-surface-2/50 border-b border-border">
                  <tr><th className="h-9 pl-4 pr-3 font-medium">Händler</th><th className="h-9 px-3 font-medium">FFN-ID</th><th className="h-9 px-3 font-medium">Im Portal</th><th className="h-9 pl-3 pr-4" /></tr>
                </thead>
                <tbody>
                  {shown.map((m) => (
                    <tr key={m.merchantId} className={cx("border-b border-border last:border-0", !m.active && "opacity-50")}>
                      <td className="pl-4 pr-3 py-2.5"><div className="font-medium"><Highlight text={m.company} needles={needles} /></div><div className="text-[12px] text-fg-muted"><Highlight text={[m.city, m.email].filter(Boolean).join(" · ")} needles={needles} />{!m.active && " · inaktiv"}</div></td>
                      <td className="px-3 py-2.5 font-mono text-[12.5px]"><Highlight text={m.merchantId} needles={needles} /></td>
                      <td className="px-3 py-2.5">
                        {m.portal ? (m.portal.status === "active" ? <Badge tone="success">{m.portal.tenantName}</Badge> : <Badge tone="warning">{m.portal.tenantName} · {m.portal.authorized ? "Lager wählen" : "Freigabe ausstehend"}</Badge>) : <Badge>noch nicht</Badge>}
                      </td>
                      <td className="pl-3 pr-4 py-2.5 text-right">
                        {m.portal ? (
                          m.portal.status === "active" ? (
                            <Button size="sm" variant="ghost" onClick={() => { setTenantId(m.portal!.tenantId); location.assign("/settings/warehouse"); }}>Öffnen</Button>
                          ) : (
                            <Button size="sm" onClick={() => setLinking({ id: m.portal!.tenantId, name: m.portal!.tenantName })}>{m.portal.authorized ? "Lager wählen" : "Freigabe anfordern"}</Button>
                          )
                        ) : m.active && isAdmin ? (
                          <Button size="sm" onClick={() => setAssigning(m)}>Zuordnen</Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {shown.length === 0 && (
                <EmptyState icon={<SearchX className="size-5" />} title={search ? `Kein Händler gefunden für „${search}“` : "Keine Händler in dieser Ansicht"} action={<Button onClick={() => { setSearch(""); setFilter("all"); }}>Suche und Filter zurücksetzen</Button>}>
                  Gesucht wird in Firma, FFN-ID, Ort, E-Mail und dem zugeordneten Portal-Kunden.
                </EmptyState>
              )}
            </Card>
            <p className="text-[12px] text-fg-subtle">Ablauf: Händler einem Kunden zuordnen → Kunde gibt per Link bei JTL frei → Lager aktiv. Verbindet ein Kunde sein FFN-Konto selbst im Portal, wird er automatisch zugeordnet.</p>
          </>
        )}
      </div>
      {assigning && (
        <AssignToCustomerDialog
          merchant={assigning}
          open
          onOpenChange={(o) => !o && setAssigning(null)}
          onCreateNew={() => { setCreate(assigning); setAssigning(null); }}
          onAssigned={(t) => { setAssigning(null); setLinking(t); }}
        />
      )}
      {create && (
        <NewCustomerDialog
          key={create.merchantId}
          open
          onOpenChange={(o) => !o && setCreate(null)}
          initialName={create.company}
          initialEmail={create.email ?? ""}
          onCreated={async (id) => {
            const m = create;
            setCreate(null);
            // Neuer Kunde bekommt den Händler direkt zugeordnet, danach Freigabe anfordern
            try {
              await api.admin.assignMerchant(id, m.merchantId, m.company);
            } catch {
              toast.error("Kunde angelegt, Zuordnung zum FFN-Händler fehlgeschlagen – bitte erneut zuordnen");
            }
            void qc.invalidateQueries({ queryKey: ["admin-ffn"] });
            void qc.invalidateQueries({ queryKey: ["admin-ffn-links"] });
            void qc.invalidateQueries({ queryKey: ["admin-tenants"] });
            void qc.invalidateQueries({ queryKey: ["me"] });
            setLinking({ id, name: m.company });
          }}
        />
      )}
      {linking && <FfnLinkDialog key={linking.id} tenant={linking} open onOpenChange={(o) => !o && setLinking(null)} />}
    </div>
  );
}

/** Artikel automatisch für Logiful freigeben – nur einschalten, wenn der Kunde zugestimmt hat */
/** Kundenkürzel bearbeiten – steht in Wareneingangsnummern und macht sie im Lager kundenübergreifend eindeutig */
/** Logiful-Kundennummer: fehlt → vergeben oder (Bestandskunde) Wawi-Nummer eintragen; ändern nur mit Bestätigung */
function CustomerNumberButton({ t }: { t: AdminTenant }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState("");
  const m = useMutation({
    mutationFn: (n: string | undefined) => api.admin.setCustomerNumber(t.id, n),
    onSuccess: (r) => {
      toast.success(`${t.name}: Kundennummer ${r.customerNumber}`);
      setEdit(false);
      void qc.invalidateQueries({ queryKey: ["admin-tenants"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => toast.error("Nicht gespeichert", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  const refs = Object.entries(t.refs ?? {});
  if (!edit)
    return (
      <Tip content={t.customerNumber ? (refs.length ? "Verknüpft: " + refs.map(([s, id]) => `${REF_LABEL[s] ?? s} ${id}`).join(" · ") : "Noch keine Verweise in anderen Systemen") : "Keine Kundennummer – vergeben oder Wawi-Nummer eintragen"}>
        <button
          onClick={() => { setV(t.customerNumber ?? ""); setEdit(true); }}
          className={cx("shrink-0 h-7 px-2 rounded-lg border font-mono text-[11.5px] tabular-nums hover:bg-surface-2", t.customerNumber ? "border-border" : "border-warning/50 text-warning")}
        >
          {t.customerNumber ?? "fehlt"}
        </button>
      </Tip>
    );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (t.customerNumber && v !== t.customerNumber && !confirm(`Kundennummer von „${t.name}“ von ${t.customerNumber} auf ${v} ändern? Alle Systeme verknüpfen über diese Nummer.`)) return;
        m.mutate(v || undefined);
      }}
      className="shrink-0 flex items-center gap-1"
    >
      <input autoFocus value={v} onChange={(e) => setV(e.target.value.replace(/[^0-9]/g, "").slice(0, 9))} onKeyDown={(e) => e.key === "Escape" && setEdit(false)} placeholder="neu" className="w-16 h-7 rounded-lg border border-border bg-surface px-2 font-mono text-[11.5px]" aria-label="Kundennummer" title="Leer lassen = nächste freie Nummer ab 10000" />
      <button type="submit" disabled={m.isPending || (v !== "" && /^0/.test(v))} className="h-7 px-2 rounded-lg bg-ink text-ink-fg text-[12px] disabled:opacity-50">OK</button>
    </form>
  );
}

const REF_LABEL: Record<string, string> = { "jtl-wawi": "Wawi", "jtl-ffn": "FFN", "retool-returns": "Retouren", "returns-portal": "Retourenportal" };

function TenantCodeButton({ t }: { t: AdminTenant }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState("");
  const m = useMutation({
    mutationFn: (code: string) => api.admin.setTenantCode(t.id, code),
    onSuccess: (r) => {
      toast.success(`${t.name}: Kürzel ${r.code}`, { description: "Neue Wareneingänge heißen z. B. WE-" + r.code + "-" + new Date().getFullYear() + "-0001. Bestehende Nummern bleiben." });
      setEdit(false);
      void qc.invalidateQueries({ queryKey: ["admin-tenants"] });
    },
    onError: (e) => toast.error("Nicht gespeichert", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  if (!edit)
    return (
      <button onClick={() => { setV(t.code ?? ""); setEdit(true); }} className="shrink-0 h-7 px-2 rounded-lg border border-border font-mono text-[11.5px] hover:bg-surface-2" title="Kundenkürzel (in Wareneingangsnummern) ändern">
        {t.code ?? "Kürzel"}
      </button>
    );
  return (
    <form onSubmit={(e) => { e.preventDefault(); m.mutate(v); }} className="shrink-0 flex items-center gap-1">
      <input autoFocus value={v} onChange={(e) => setV(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))} onKeyDown={(e) => e.key === "Escape" && setEdit(false)} className="w-20 h-7 rounded-lg border border-border bg-surface px-2 font-mono text-[11.5px]" aria-label="Kundenkürzel" />
      <button type="submit" disabled={m.isPending || v.length < 2} className="h-7 px-2 rounded-lg bg-ink text-ink-fg text-[12px] disabled:opacity-50">OK</button>
    </form>
  );
}

/** Selbst registriert (Website/Shopify-App): Kontakt und Angaben fürs Angebot */
function SignupBadge({ s }: { s: NonNullable<AdminTenant["signup"]> }) {
  const lines = [`${s.source === "shopify" ? `Über die Shopify-App (${s.shop})` : "Über die Website"} am ${new Date(s.at).toLocaleDateString(intlLocale())}`, `Kontakt: ${s.contactName}, ${s.email}${s.phone ? `, ${s.phone}` : ""}`, ...signupOfferLines(s.offer)];
  return (
    <Tip content={<span className="block space-y-0.5">{lines.map((l) => <span key={l} className="block">{l}</span>)}</span>}>
      <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-coral/10 px-1.5 py-px text-[11px] font-medium text-coral">Selbst registriert{s.offer ? " · Angebot" : ""}</span>
    </Tip>
  );
}

/** Testbetrieb (Hardlock) je Kunde – Einschalten ist gefahrlos, Ausschalten geht live */
function TestModeToggle({ t }: { t: AdminTenant }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (enabled: boolean) => api.admin.setTestMode(t.id, enabled),
    onSuccess: (r) => {
      toast.success(r.enabled ? `${t.name}: Testbetrieb an – nichts geht ans Lager oder in den Shop` : `${t.name}: Testbetrieb beendet – ab jetzt live`);
      void qc.invalidateQueries({ queryKey: ["admin-tenants"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: () => toast.error("Testbetrieb konnte nicht umgestellt werden"),
  });
  const on = m.isPending ? !!m.variables : !!t.testModeSince;
  return (
    <Tip content={on ? `Seit ${new Date(t.testModeSince ?? Date.now()).toLocaleDateString(intlLocale())}: Aufträge werden geprüft, aber nie ans Lager übergeben; keine Bestände/Standort an Shops` : "Live-Betrieb"}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`Testbetrieb für ${t.name}`}
        onClick={() => {
          if (on && !confirm(`Testbetrieb für „${t.name}“ beenden und live gehen?

• Neue Aufträge gehen ab jetzt ans Lager.
• Bestände werden an die Shops gemeldet, der Logiful-Standort wird eingerichtet.
• Im Testbetrieb eingegangene Aufträge bleiben Testaufträge und gehen nie ans Lager.`)) return;
          if (!on && !confirm(`Testbetrieb für „${t.name}“ einschalten?

Noch nicht übertragene Aufträge gehen dann nie ans Lager – auch nicht nach dem Testbetrieb.`)) return;
          m.mutate(!on);
        }}
        className="inline-flex shrink-0 items-center gap-1.5 text-[12px] text-fg-muted"
      >
        <span className={cx("w-7 h-4 rounded-full p-0.5 transition-colors", on ? "bg-warning" : "bg-border-strong")}><span className={cx("block size-3 rounded-full bg-surface transition-transform", on && "translate-x-3")} /></span>
        {on ? "an" : "aus"}
      </button>
    </Tip>
  );
}

function AutoReleaseToggle({ t }: { t: AdminTenant }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (enabled: boolean) => api.admin.setAutoRelease(t.id, enabled),
    onSuccess: (r) => {
      toast.success(r.enabled ? `${t.name}: Artikel werden automatisch für Logiful freigegeben` : `${t.name}: automatische Freigabe aus`);
      void qc.invalidateQueries({ queryKey: ["admin-tenants"] });
    },
    onError: () => toast.error("Einstellung konnte nicht gespeichert werden"),
  });
  const on = m.isPending ? !!m.variables : !!t.autoReleaseProducts;
  return (
    <Tip content={on ? "Übertragene Artikel werden in FFN automatisch für Logiful freigegeben" : "Aus: Der Kunde gibt Artikel in FFN selbst für Logiful frei. Nur einschalten, wenn der Kunde zugestimmt hat."}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`Automatische Artikelfreigabe für ${t.name}`}
        onClick={() => {
          if (!on && !confirm(`Artikel von „${t.name}“ künftig automatisch in FFN für Logiful freigeben? Bitte nur mit Zustimmung des Kunden.`)) return;
          m.mutate(!on);
        }}
        className="inline-flex shrink-0 items-center gap-1.5 text-[12px] text-fg-muted"
      >
        <span className={cx("w-7 h-4 rounded-full p-0.5 transition-colors", on ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3 rounded-full bg-surface transition-transform", on && "translate-x-3")} /></span>
        {on ? "an" : "aus"}
      </button>
    </Tip>
  );
}
